import { type Writable, writable, get, derived } from 'svelte/store';
import type { Quantity, Dose, Form, Drug } from '../utils/drug-utils';

export const drug: Writable<Drug | null> = writable(null);

export const formValue: Writable<string> = writable('');

export const strengthValue: Writable<string> = writable('');

export const quantityValue: Writable<string> = writable('');

export const customQuantityValue: Writable<string> = writable('');

export const getDrug = (): Drug | null => get(drug);

export const getForm = (): Form | null => {
  const currDrug = getDrug();
  if (!currDrug) return null;

  return (
    currDrug.forms.find((form) => form.value === get(formValue)) ||
    currDrug.forms.find((form) => form.value === currDrug.defaultForm) ||
    currDrug.forms[0]
  );
};

export const getStrength = (): Dose | null => {
  const currDrug = getDrug();
  const currForm = getForm();
  if (!currDrug || !currForm) return null;

  return (
    currForm.doses.find((dose) => dose.value === get(strengthValue)) ||
    currForm.doses.find((dose) => dose.value === currDrug.defaultDose) ||
    currForm.doses[0]
  );
};

export const getCustomQuantity = (): Quantity | null => {
  const currStr = getStrength();

  const currCustomQuantity = get(customQuantityValue);

  if (!currStr) return null;

  const customQty = parseFloat(currCustomQuantity) * parseFloat(currStr.baseValue) || 0;

  return {
    label: currCustomQuantity ? `${currCustomQuantity} ${currStr.unitLabel}` : '',
    value: customQty.toString() || '',
    isCustomQuantity: true,
  };
};

export const getQuantity = (): Quantity | null => {
  const currStr = getStrength();

  if (!currStr) return null;

  if (get(quantityValue) === 'custom') {
    return getCustomQuantity();
  }

  return (
    currStr.quantity.find((qty) => qty.value === get(quantityValue)) ||
    currStr.quantity.find((qty) => qty.value === currStr.defaultQuantity) ||
    currStr.quantity[0]
  );
};

export const setDrug = (value: Drug): void => {
  drug.set(value);
};

export const setForm = (form: Form): void => {
  formValue.set(form.value);
  strengthValue.set('');
  quantityValue.set('');
  customQuantityValue.set('');
};

export const setStrength = (strength: Dose): void => {
  strengthValue.set(strength.value);
};

export const setQuantity = (quantity: Quantity): void => {
  quantityValue.set(quantity.isCustomQuantity ? 'custom' : quantity.value);
};

export const setCustomQuantity = (quantity?: Quantity): void => {
  if (!quantity || !quantity.value) {
    quantityValue.set('');
    customQuantityValue.set('');
  } else {
    quantityValue.set('custom');
    customQuantityValue.set(quantity.value);
  }
};

export const clearCustomQuantity = (): void => {
  customQuantityValue.set('');
};

export const drugData = derived([drug, formValue, strengthValue, quantityValue, customQuantityValue], () => ({
  drug: getDrug(),
  form: getForm(),
  strength: getStrength(),
  quantity: getQuantity(),
  customQuantity: getCustomQuantity(),
}));
